import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { useAuth } from "@/contexts/AuthContext";
import { useWs } from "@/contexts/WebSocketContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Key, QrCode, Camera, MagnifyingGlass, ArrowClockwise, FileXls, FilePdf,
  Trash, Pencil, X as XIcon, Check, ArrowUUpLeft, Printer, WarningCircle
} from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";
import { QRCodeSVG } from "qrcode.react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDT(iso) {
  if (!iso) return "\u2014";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    // Force GMT+8 (Asia/Singapore) display regardless of the viewer's browser timezone
    return d.toLocaleString("en-GB", {
      timeZone: "Asia/Singapore",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }) + " GMT+8";
  } catch (e) { return iso; }
}

function fullUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${REACT_APP_BACKEND_URL}${u}`;
}

// Reusable QR scanner using html5-qrcode; camera-based
function QrScannerDialog({ open, onClose, onScanned }) {
  const scannerRef = useRef(null);
  const containerRef = useRef(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  // Latest onScanned kept in a ref so we don't retrigger the whole effect on every parent re-render
  const onScannedRef = useRef(onScanned);
  useEffect(() => { onScannedRef.current = onScanned; }, [onScanned]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError("");
    setStarting(true);

    const safeStopAndClear = async (inst) => {
      if (!inst) return;
      try {
        if (typeof inst.isScanning === "boolean" ? inst.isScanning : true) {
          await inst.stop();
        }
      } catch (e) { /* ignore stop errors */ }
      try { inst.clear(); } catch (e) { /* ignore clear errors */ }
    };

    const start = async () => {
      // Wait up to 800ms for the container div to be in the DOM
      let el = containerRef.current;
      for (let i = 0; i < 8 && !el; i++) {
        await new Promise((r) => setTimeout(r, 100));
        el = containerRef.current;
      }
      if (cancelled || !el) {
        setStarting(false);
        return;
      }
      // Ensure the container has an id (html5-qrcode requires an id)
      if (!el.id) el.id = `km-qr-reader-${Math.random().toString(36).slice(2, 8)}`;

      try {
        const inst = new Html5Qrcode(el.id);
        scannerRef.current = inst;
        await inst.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (cancelled) return;
            cancelled = true;
            safeStopAndClear(inst);
            const cb = onScannedRef.current;
            if (cb) cb(decoded);
          },
          () => { /* per-frame decode failures — ignore */ }
        );
      } catch (e) {
        // Common causes: no camera permission, no camera, non-HTTPS, browser doesn't support getUserMedia
        setError("Camera unavailable — allow camera access or type the key manually below.");
        // Ensure the (possibly partially-created) instance is cleaned up
        await safeStopAndClear(scannerRef.current);
        scannerRef.current = null;
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    start();

    return () => {
      cancelled = true;
      const inst = scannerRef.current;
      scannerRef.current = null;
      // Fire-and-forget cleanup; wrap in Promise.resolve so any synchronous throws inside
      // isScanning getter or stop() are caught by the .catch below.
      Promise.resolve().then(() => safeStopAndClear(inst)).catch(() => {});
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
            <QrCode size={20} weight="bold" className="text-[#007AFF]" /> SCAN QR — KEY VALUE
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div ref={containerRef} id="km-qr-reader" className="w-full min-h-[260px] bg-black rounded-sm border border-[#262626] overflow-hidden" data-testid="km-qr-container" />
          {starting && <p className="text-xs text-[#737373]">Starting camera…</p>}
          {error && <p className="text-xs text-[#FF3B30]" data-testid="km-qr-error">{error}</p>}
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Or type key value manually</Label>
            <div className="flex gap-2">
              <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="e.g. K-101"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="km-manual-key-input" />
              <Button onClick={() => { if (manual.trim()) onScanned(manual.trim()); }}
                className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white rounded-sm text-xs" data-testid="km-manual-key-use-btn">
                <Check size={14} className="mr-1" /> Use
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] rounded-sm" data-testid="km-qr-cancel-btn">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Client-side image compression via canvas. Downscales to max 1280px on the long side, JPEG q=0.75.
async function compressImageFile(file, maxDimension = 1280, quality = 0.75) {
  // Skip compression for non-images or already-tiny files
  if (!file.type.startsWith("image/") || file.size < 200 * 1024) {
    return file;
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob || blob.size >= file.size) {
    // Compression didn't help; keep original
    return file;
  }
  return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg", lastModified: Date.now() });
}

// Reusable photo capture — uses the device's camera via file input
function PhotoCapture({ label, value, onChange, disabled, testId }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const original = e.target.files?.[0];
    if (!original) return;
    if (original.size > 15 * 1024 * 1024) {
      toast.error("File too large. Max 15MB.");
      return;
    }
    setUploading(true);
    try {
      const file = await compressImageFile(original);
      const savedKb = Math.round((original.size - file.size) / 1024);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/key-management/photo-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      if (savedKb > 5) {
        toast.success(`Photo uploaded (compressed, saved ${savedKb} KB)`);
      } else {
        toast.success("Photo uploaded");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Photo upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">{label}</Label>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" data-testid={`${testId}-file`} />
      <div className="flex items-center gap-3">
        <div className="w-24 h-24 rounded-sm bg-[#0A0A0A] border border-[#262626] flex items-center justify-center overflow-hidden">
          {value ? (
            <img src={fullUrl(value)} alt="Captured" className="w-full h-full object-cover" />
          ) : (
            <Camera size={28} className="text-[#404040]" />
          )}
        </div>
        <Button type="button" onClick={pick} disabled={disabled || uploading}
          className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white rounded-sm text-xs" data-testid={testId}>
          <Camera size={14} className="mr-1" weight="bold" /> {uploading ? "Uploading…" : (value ? "Retake" : "Take Photo")}
        </Button>
        {value && !disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} className="text-[#737373] hover:text-white h-8" data-testid={`${testId}-clear`}>
            <XIcon size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}

export function KeyManagementTab() {
  const { user } = useAuth();
  const ws = useWs();
  const isSuper = user?.role === "superadmin";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [form, setForm] = useState({ key_value: "", taken_by_name: "", taken_by_photo: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  // Return dialog
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnForm, setReturnForm] = useState({ returned_by_name: "", returned_by_photo: "" });
  const [returning, setReturning] = useState(false);
  // View photo
  const [viewPhoto, setViewPhoto] = useState(null);
  // Edit / Delete (superadmin)
  const [editRec, setEditRec] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [exporting, setExporting] = useState("");
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");
  const [exportOpen, setExportOpen] = useState(true);
  const [highlightId, setHighlightId] = useState(null);
  // QR Labels (superadmin)
  const [qrLabelsOpen, setQrLabelsOpen] = useState(false);
  const [definitions, setDefinitions] = useState([]);
  const [defForm, setDefForm] = useState({ key_value: "", label: "" });
  const [addingDef, setAddingDef] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  // Overdue threshold
  const [overdueHours, setOverdueHours] = useState(24);
  const [overdueDraft, setOverdueDraft] = useState("24");
  const [savingOverdue, setSavingOverdue] = useState(false);
  // Error Key message (customizable by admin)
  const [errorMsg, setErrorMsg] = useState("This key is already taken and has not been returned yet.");
  const [errorMsgDraft, setErrorMsgDraft] = useState("");
  const [savingErrorMsg, setSavingErrorMsg] = useState(false);
  const [duplicateCheckEnabled, setDuplicateCheckEnabled] = useState(true);
  const [savingDupCheck, setSavingDupCheck] = useState(false);
  // Take-Key duplicate check
  const [keyCheckState, setKeyCheckState] = useState({ status: "idle", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/key-management/records?${params.toString()}`);
      setRecords(data.records || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load records");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/key-management/settings");
      setOverdueHours(data.overdue_hours);
      setOverdueDraft(String(data.overdue_hours));
      if (data.error_message) {
        setErrorMsg(data.error_message);
        setErrorMsgDraft(data.error_message);
      }
      if (typeof data.duplicate_check_enabled === "boolean") {
        setDuplicateCheckEnabled(data.duplicate_check_enabled);
      }
    } catch (err) { /* ignore */ }
  }, []);

  const loadDefinitions = useCallback(async () => {
    try {
      const { data } = await api.get("/key-management/definitions");
      setDefinitions(data.definitions || []);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => { loadSettings(); loadDefinitions(); }, [loadSettings, loadDefinitions]);

  // Real-time updates: overdue alerts + record change events
  useEffect(() => {
    if (!ws?.on) return undefined;
    const offOverdue = ws.on("key_overdue_alert", (data) => {
      toast.error(
        `Key OVERDUE — ${data.key_value} · ${data.taken_by_name}`,
        { duration: 8000, id: `overdue-${data.record_id}` }
      );
      load();
    });
    const offChange = ws.on("key_record_change", (data) => {
      // Silent live update — reload the list so all connected clients stay in sync
      load();
      // Flash the changed row (unless it was deleted)
      if (data.action !== "deleted" && data.record_id) {
        setHighlightId(data.record_id);
        setTimeout(() => setHighlightId(null), 2500);
      }
    });
    return () => { offOverdue(); offChange(); };
  }, [ws, load]);

  // Highlight a record when navigated from Audit Log
  useEffect(() => {
    const handler = (e) => {
      const { tab, highlightId: hid } = e.detail || {};
      if (tab !== "key-management" || !hid) return;
      setHighlightId(hid);
      setTimeout(() => {
        const row = document.querySelector(`[data-testid="km-row-${hid}"]`);
        if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
      // Clear highlight after 4s
      setTimeout(() => setHighlightId(null), 4000);
    };
    window.addEventListener("admin-highlight", handler);
    return () => window.removeEventListener("admin-highlight", handler);
  }, []);
  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  const isOverdue = useCallback((rec) => {
    if (rec.status !== "taken" || !rec.taken_at) return false;
    try {
      const takenMs = new Date(rec.taken_at).getTime();
      const nowMs = Date.now();
      return (nowMs - takenMs) / 3600000 > overdueHours;
    } catch (e) { return false; }
  }, [overdueHours]);

  const saveOverdueHours = async () => {
    const val = parseFloat(overdueDraft);
    if (isNaN(val) || val < 0.1 || val > 720) { toast.error("Enter 0.1 to 720 hours"); return; }
    setSavingOverdue(true);
    try {
      await api.put("/key-management/settings", { hours: val });
      setOverdueHours(val);
      toast.success(`Overdue threshold set to ${val}h`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingOverdue(false);
    }
  };

  const saveErrorMessage = async () => {
    const val = errorMsgDraft.trim();
    if (!val) { toast.error("Error message cannot be empty"); return; }
    if (val === errorMsg) return;
    setSavingErrorMsg(true);
    try {
      const { data } = await api.put("/key-management/settings", { error_message: val });
      setErrorMsg(data.error_message);
      toast.success("Error message saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingErrorMsg(false);
    }
  };

  const toggleDuplicateCheck = async (next) => {
    setSavingDupCheck(true);
    try {
      const { data } = await api.put("/key-management/settings", { duplicate_check_enabled: next });
      setDuplicateCheckEnabled(data.duplicate_check_enabled);
      toast.success(next ? "Duplicate-key check ON" : "Duplicate-key check OFF (transactions will bypass)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingDupCheck(false);
    }
  };

  const checkKeyAvailable = async (value) => {
    const v = (value || "").trim();
    if (!v) { setKeyCheckState({ status: "idle", message: "" }); return true; }
    setKeyCheckState({ status: "checking", message: "" });
    try {
      const { data } = await api.get(`/key-management/check?value=${encodeURIComponent(v)}`);
      if (data.available) {
        setKeyCheckState({ status: "ok", message: "" });
        return true;
      }
      setKeyCheckState({ status: "blocked", message: data.message || errorMsg });
      return false;
    } catch (err) {
      setKeyCheckState({ status: "idle", message: "" });
      return true; // don't block on network error
    }
  };

  const addDefinition = async () => {
    const kv = defForm.key_value.trim();
    if (!kv) { toast.error("Key value required"); return; }
    setAddingDef(true);
    try {
      await api.post("/key-management/definitions", { key_value: kv, label: defForm.label.trim() });
      setDefForm({ key_value: "", label: "" });
      await loadDefinitions();
      toast.success(`Added key '${kv}'`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Add failed");
    } finally {
      setAddingDef(false);
    }
  };

  const parseBulkEntries = (text) => {
    // Accept: CSV (key,label), TSV, or newline-separated key values.
    return text.split(/\r?\n/).map((line) => {
      const raw = line.trim();
      if (!raw) return null;
      // Split on comma or tab (first delimiter only, so labels may contain spaces)
      const parts = raw.split(/[,\t]/);
      const key_value = (parts[0] || "").trim();
      const label = parts.slice(1).join(",").trim();
      if (!key_value) return null;
      return { key_value, label };
    }).filter(Boolean);
  };

  const submitBulk = async () => {
    const entries = parseBulkEntries(bulkText);
    if (entries.length === 0) { toast.error("Paste at least one key value"); return; }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/key-management/definitions/bulk", { entries });
      setBulkResult(data);
      toast.success(`Added ${data.added_count}; skipped ${data.skipped_count} duplicate(s)`);
      await loadDefinitions();
      setBulkText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk add failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const deleteDefinition = async (id) => {
    try {
      await api.delete(`/key-management/definitions/${id}`);
      await loadDefinitions();
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const printQrLabels = () => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("Popup blocked - please allow popups to print"); return; }
    const labelsHtml = definitions.map((d) => {
      const svg = document.getElementById(`qr-preview-${d._id}`)?.outerHTML || "";
      return `<div class="label"><div class="qr">${svg}</div><div class="text"><div class="kv">${d.key_value}</div>${d.label ? `<div class="lbl">${d.label}</div>` : ""}</div></div>`;
    }).join("");
    win.document.write(`<!DOCTYPE html><html><head><title>Key QR Labels</title><style>
      @page { margin: 12mm; }
      body { font-family: system-ui, sans-serif; margin: 0; padding: 12px; }
      .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
      .label { border: 1px dashed #999; padding: 10px; text-align: center; page-break-inside: avoid; }
      .qr svg { width: 140px; height: 140px; }
      .kv { font-family: 'Courier New', monospace; font-weight: 700; font-size: 16px; margin-top: 6px; }
      .lbl { font-size: 12px; color: #444; margin-top: 2px; }
      @media print { .no-print { display: none; } }
    </style></head><body><div class="no-print" style="margin-bottom:10px;"><button onclick="window.print()">Print</button></div><div class="grid">${labelsHtml}</div></body></html>`);
    win.document.close();
  };

  const openCreate = () => {
    setForm({ key_value: "", taken_by_name: "", taken_by_photo: "", remarks: "" });
    setKeyCheckState({ status: "idle", message: "" });
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!form.key_value.trim()) { toast.error("Key value is required (scan QR or type)"); return; }
    if (!form.taken_by_name.trim()) { toast.error("Key Taken By name is required"); return; }
    if (!form.taken_by_photo) { toast.error("Taken By photo is required"); return; }
    setSaving(true);
    try {
      await api.post("/key-management/records", form);
      toast.success(`Key '${form.key_value}' recorded`);
      setCreateOpen(false);
      setForm({ key_value: "", taken_by_name: "", taken_by_photo: "", remarks: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openReturn = (rec) => {
    setReturnTarget(rec);
    setReturnForm({ returned_by_name: "", returned_by_photo: "" });
  };

  const submitReturn = async () => {
    if (!returnForm.returned_by_name.trim()) { toast.error("Returned By name is required"); return; }
    if (!returnForm.returned_by_photo) { toast.error("Returned By photo is required"); return; }
    setReturning(true);
    try {
      await api.post(`/key-management/records/${returnTarget._id}/return`, returnForm);
      toast.success(`Key '${returnTarget.key_value}' returned`);
      setReturnTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Return failed");
    } finally {
      setReturning(false);
    }
  };

  const openEdit = (rec) => {
    setEditRec(rec);
    setEditForm({
      key_value: rec.key_value || "",
      taken_by_name: rec.taken_by_name || "",
      taken_at: rec.taken_at || "",
      returned_by_name: rec.returned_by_name || "",
      returned_at: rec.returned_at || "",
      status: rec.status || "taken",
      remarks: rec.remarks || "",
    });
  };

  const submitEdit = async () => {
    setSavingEdit(true);
    try {
      let payload;
      if (isSuper) {
        payload = { ...editForm };
        ["taken_at", "returned_at"].forEach((k) => {
          if (payload[k] && !payload[k].includes("T")) return;
          if (payload[k]) {
            try { payload[k] = new Date(payload[k]).toISOString(); } catch (e) {}
          }
        });
      } else {
        // Regular users can only edit name fields and remarks
        payload = {
          taken_by_name: editForm.taken_by_name || "",
          returned_by_name: editForm.returned_by_name || "",
          remarks: editForm.remarks || "",
        };
      }
      await api.put(`/key-management/records/${editRec._id}`, payload);
      toast.success("Record updated");
      setEditRec(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const submitDelete = async () => {
    try {
      await api.delete(`/key-management/records/${confirmDelete._id}`);
      toast.success("Record deleted");
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (exportStart) params.set("start_date", exportStart);
      if (exportEnd) params.set("end_date", exportEnd);
      const url = `/key-management/export.${fmt}?${params.toString()}`;
      const response = await api.get(url, { responseType: "blob" });
      const mime = fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const blob = new Blob([response.data], { type: mime });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const rangeSuffix = (exportStart || exportEnd) ? `_${exportStart || "all"}_to_${exportEnd || "all"}` : "";
      a.download = `key_records${rangeSuffix}_${new Date().toISOString().slice(0, 10)}_GMT8.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success(`Exported as ${fmt.toUpperCase()} (GMT+8)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
    } finally {
      setExporting("");
    }
  };

  const toDatetimeLocal = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) { return ""; }
  };

  return (
    <div className="space-y-4">
      {/* Header actions */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
              <Key size={16} weight="bold" className="text-[#FACC15]" /> Key Management
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={openCreate} className="bg-[#FACC15] hover:bg-[#FACC15]/80 text-black rounded-sm text-xs font-semibold" data-testid="km-take-key-btn">
                <QrCode size={14} className="mr-1" weight="bold" /> Take Key (Scan QR)
              </Button>
              {isSuper && (
                <Button variant="outline" onClick={() => setQrLabelsOpen(true)}
                  className="border-[#262626] bg-[#0A0A0A] text-white hover:bg-[#1A1A1A] rounded-sm text-xs" data-testid="km-qr-labels-btn">
                  <Printer size={14} className="mr-1" /> QR Labels
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#737373]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by key value, taken by, returned by…"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm pl-8 font-mono-data"
                data-testid="km-search-input" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#0A0A0A] border border-[#262626] text-white rounded-sm px-3 py-2 text-xs h-9"
              data-testid="km-status-filter">
              <option value="">All statuses</option>
              <option value="taken">Taken (out)</option>
              <option value="returned">Returned</option>
            </select>
            <Button variant="ghost" size="sm" onClick={load} className="text-[#737373] hover:text-white h-9" data-testid="km-refresh-btn">
              <ArrowClockwise size={14} className="mr-1" /> Refresh
            </Button>
          </div>
          {/* Overdue threshold + Error Key */}
          <div className="mt-3 flex items-center gap-4 flex-wrap text-xs">
            <div className="flex items-center gap-1.5 text-[#A3A3A3]">
              <WarningCircle size={14} weight="bold" className="text-[#FF3B30]" />
              <span>Overdue after:</span>
              {isSuper ? (
                <>
                  <Input type="number" step="0.5" min="0.1" max="720" value={overdueDraft}
                    onChange={(e) => setOverdueDraft(e.target.value)}
                    onBlur={() => { if (parseFloat(overdueDraft) !== overdueHours) saveOverdueHours(); }}
                    className="w-20 h-7 bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs font-mono-data"
                    disabled={savingOverdue} data-testid="km-overdue-hours-input" />
                  <span>hours</span>
                </>
              ) : (
                <span className="font-mono-data text-white">{overdueHours} hours</span>
              )}
            </div>
            {isSuper && (
              <div className="flex items-center gap-1.5 text-[#A3A3A3] flex-1 min-w-[280px]">
                <WarningCircle size={14} weight="bold" className="text-[#FACC15]" />
                <span className="whitespace-nowrap">Error Key message:</span>
                <Input value={errorMsgDraft}
                  onChange={(e) => setErrorMsgDraft(e.target.value)}
                  onBlur={saveErrorMessage}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
                  placeholder="Shown when scanning an already-taken key"
                  className="h-7 bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs flex-1 min-w-[180px]"
                  disabled={savingErrorMsg} data-testid="km-error-message-input" />
              </div>
            )}
          </div>
          {isSuper && (
            <div className="mt-2 flex items-center gap-3 flex-wrap text-xs p-2 bg-[#0A0A0A] border border-[#262626] rounded-sm">
              <div className="flex items-center gap-2">
                <Switch checked={duplicateCheckEnabled} onCheckedChange={toggleDuplicateCheck}
                  disabled={savingDupCheck} data-testid="km-duplicate-check-toggle" />
                <span className={`font-semibold ${duplicateCheckEnabled ? "text-[#32D74B]" : "text-[#FF9500]"}`}>
                  Duplicate-key check: {duplicateCheckEnabled ? "ON" : "OFF (bypass)"}
                </span>
              </div>
              <span className="text-[10px] text-[#737373] flex-1 min-w-[240px]">
                {duplicateCheckEnabled
                  ? "Scanning an already-taken key shows the error and blocks Save."
                  : "⚠ Any scanned key is allowed — the same key can be taken repeatedly."}
              </span>
            </div>
          )}
          {/* Collapsible Export bar (filters + buttons in one row) */}
          <div className="mt-2 border border-[#262626] rounded-sm">
            <button type="button" onClick={() => setExportOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#0F0F0F] transition text-left"
              data-testid="km-export-collapse-toggle">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1.5">
                <FileXls size={12} /> Export (GMT+8)
                {(exportStart || exportEnd) && <span className="text-[#FACC15] normal-case tracking-normal">— {exportStart || "any"} → {exportEnd || "any"}</span>}
              </span>
              <span className="text-[10px] text-[#737373]">{exportOpen ? "▾ hide" : "▸ show"}</span>
            </button>
            {exportOpen && (
              <div className="p-3 border-t border-[#262626] flex items-end gap-2 flex-wrap bg-[#0A0A0A]">
                <div className="space-y-0.5">
                  <Label className="text-[9px] uppercase tracking-[0.15em] text-[#737373]">From</Label>
                  <Input type="date" value={exportStart} onChange={(e) => setExportStart(e.target.value)}
                    className="w-[150px] h-8 bg-[#121212] border-[#262626] text-white rounded-sm text-xs font-mono-data"
                    data-testid="km-export-start-date" />
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[9px] uppercase tracking-[0.15em] text-[#737373]">To</Label>
                  <Input type="date" value={exportEnd} onChange={(e) => setExportEnd(e.target.value)}
                    className="w-[150px] h-8 bg-[#121212] border-[#262626] text-white rounded-sm text-xs font-mono-data"
                    data-testid="km-export-end-date" />
                </div>
                {(exportStart || exportEnd) && (
                  <Button variant="ghost" size="sm" onClick={() => { setExportStart(""); setExportEnd(""); }}
                    className="text-[#737373] hover:text-white text-[10px] h-8"
                    data-testid="km-export-clear-dates">
                    <XIcon size={12} className="mr-1" /> Clear
                  </Button>
                )}
                <div className="flex-1" />
                <Button variant="outline" onClick={() => doExport("xlsx")} disabled={!!exporting}
                  className="border-[#262626] bg-[#121212] text-white hover:bg-[#1A1A1A] rounded-sm text-xs h-8"
                  data-testid="km-export-xlsx-btn">
                  <FileXls size={14} className="mr-1" /> {exporting === "xlsx" ? "Exporting…" : "Export XLS"}
                </Button>
                <Button variant="outline" onClick={() => doExport("pdf")} disabled={!!exporting}
                  className="border-[#262626] bg-[#121212] text-white hover:bg-[#1A1A1A] rounded-sm text-xs h-8"
                  data-testid="km-export-pdf-btn">
                  <FilePdf size={14} className="mr-1" /> {exporting === "pdf" ? "Exporting…" : "Export PDF"}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Records table */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#262626] hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Key Value</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Taken By</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Taken Photo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Taken At</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Returned By</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Returned Photo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Returned At</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373]">Remarks</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#737373] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center text-[#737373] text-xs py-6">Loading…</TableCell></TableRow>
              ) : records.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-[#737373] text-xs py-6">No records found.</TableCell></TableRow>
              ) : records.map((r) => {
                const overdue = isOverdue(r);
                const isHighlighted = highlightId === r._id;
                return (
                <TableRow key={r._id} className={`border-[#262626] hover:bg-[#0A0A0A] transition-colors ${isHighlighted ? "bg-[#007AFF]/20 ring-2 ring-[#007AFF]" : overdue ? "bg-[#FF3B30]/15 animate-blink-alert" : ""}`} data-testid={`km-row-${r._id}`}>
                  <TableCell className={`font-mono-data font-semibold ${overdue ? "text-[#FF3B30]" : "text-white"}`}>{r.key_value}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider border ${
                        r.status === "returned"
                          ? "bg-[#32D74B]/15 text-[#32D74B] border-[#32D74B]/40"
                          : "bg-[#FACC15]/15 text-[#FACC15] border-[#FACC15]/40"
                      }`}>{r.status}</span>
                      {overdue && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/50 animate-blink-alert" data-testid={`km-overdue-badge-${r._id}`}>
                          <WarningCircle size={10} weight="bold" /> OVERDUE
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-white text-xs">{r.taken_by_name}</TableCell>
                  <TableCell>
                    {r.taken_by_photo ? (
                      <button onClick={() => setViewPhoto({ url: r.taken_by_photo, label: `Taken by ${r.taken_by_name}` })}
                        className="w-10 h-10 rounded-sm overflow-hidden border border-[#262626] hover:border-[#007AFF]" data-testid={`km-view-taken-photo-${r._id}`}>
                        <img src={fullUrl(r.taken_by_photo)} alt="taken" className="w-full h-full object-cover" />
                      </button>
                    ) : <span className="text-[#404040]">—</span>}
                  </TableCell>
                  <TableCell className="text-[#A3A3A3] text-xs font-mono-data">{fmtDT(r.taken_at)}</TableCell>
                  <TableCell className="text-white text-xs">{r.returned_by_name || <span className="text-[#404040]">—</span>}</TableCell>
                  <TableCell>
                    {r.returned_by_photo ? (
                      <button onClick={() => setViewPhoto({ url: r.returned_by_photo, label: `Returned by ${r.returned_by_name}` })}
                        className="w-10 h-10 rounded-sm overflow-hidden border border-[#262626] hover:border-[#32D74B]" data-testid={`km-view-returned-photo-${r._id}`}>
                        <img src={fullUrl(r.returned_by_photo)} alt="returned" className="w-full h-full object-cover" />
                      </button>
                    ) : <span className="text-[#404040]">—</span>}
                  </TableCell>
                  <TableCell className="text-[#A3A3A3] text-xs font-mono-data">{r.returned_at ? fmtDT(r.returned_at) : <span className="text-[#404040]">—</span>}</TableCell>
                  <TableCell className="text-[#A3A3A3] text-xs max-w-[180px]">
                    {r.remarks ? (
                      <span className="line-clamp-2 whitespace-pre-wrap break-words" title={r.remarks} data-testid={`km-row-remarks-${r._id}`}>{r.remarks}</span>
                    ) : <span className="text-[#404040]">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {r.status !== "returned" && (
                        <Button size="sm" onClick={() => openReturn(r)}
                          className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black rounded-sm text-[10px] h-7 px-2 font-semibold" data-testid={`km-return-btn-${r._id}`}>
                          <ArrowUUpLeft size={12} className="mr-1" weight="bold" /> Return
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                        className="text-[#A3A3A3] hover:text-white h-7 w-7 p-0" data-testid={`km-edit-btn-${r._id}`}>
                        <Pencil size={12} />
                      </Button>
                      {isSuper && (
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}
                          className="text-[#FF3B30] hover:text-[#FF3B30] h-7 w-7 p-0" data-testid={`km-delete-btn-${r._id}`}>
                          <Trash size={12} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) setCreateOpen(false); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Key size={20} weight="bold" className="text-[#FACC15]" /> TAKE KEY
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Key Value (from QR)</Label>
              <div className="flex gap-2">
                <Input value={form.key_value} readOnly placeholder="Scan QR to fill…"
                  className={`bg-[#0A0A0A] rounded-sm font-mono-data flex-1 ${keyCheckState.status === "blocked" ? "border-[#FF3B30] text-[#FF3B30]" : "border-[#262626] text-white"}`} data-testid="km-key-value-input" />
                <Button onClick={() => setQrOpen(true)}
                  className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white rounded-sm text-xs" data-testid="km-open-scanner-btn">
                  <QrCode size={14} className="mr-1" weight="bold" /> Scan QR
                </Button>
              </div>
              {keyCheckState.status === "blocked" && (
                <div className="p-2 bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-sm flex items-start gap-2" data-testid="km-key-blocked-banner">
                  <WarningCircle size={14} weight="bold" className="text-[#FF3B30] mt-0.5 shrink-0" />
                  <p className="text-xs text-[#FF3B30]">{keyCheckState.message}</p>
                </div>
              )}
              {keyCheckState.status === "checking" && (
                <p className="text-[10px] text-[#737373]">Checking key availability…</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Key Taken By <span className="text-[#FF3B30]">*</span></Label>
              <Input value={form.taken_by_name} onChange={(e) => setForm({ ...form, taken_by_name: e.target.value })}
                placeholder="Full name"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="km-taken-by-name-input" />
            </div>
            <PhotoCapture label="Taken By (Photo) *" value={form.taken_by_photo}
              onChange={(v) => setForm({ ...form, taken_by_photo: v })} testId="km-taken-by-photo" />
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Remarks <span className="text-[#737373] normal-case tracking-normal">(optional)</span></Label>
              <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                rows={3} placeholder="e.g. Spare copy, key ring damaged, etc."
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs"
                data-testid="km-remarks-input" />
            </div>
            <p className="text-[10px] text-[#737373]">Date &amp; time will be auto-stamped when you save.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] rounded-sm" data-testid="km-create-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitCreate} disabled={saving || keyCheckState.status === "blocked" || keyCheckState.status === "checking" || !form.key_value.trim()}
              className="bg-[#FACC15] hover:bg-[#FACC15]/80 text-black rounded-sm font-semibold" data-testid="km-create-save-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QrScannerDialog open={qrOpen} onClose={() => setQrOpen(false)}
        onScanned={async (val) => {
          setQrOpen(false);
          setForm((f) => ({ ...f, key_value: val }));
          const ok = await checkKeyAvailable(val);
          if (ok) {
            toast.success(`Scanned: ${val}`);
          } else {
            toast.error(errorMsg);
            // Keep the blocked value visible; Save is disabled by keyCheckState==='blocked'
          }
        }} />

      {/* Return dialog */}
      <Dialog open={!!returnTarget} onOpenChange={(o) => { if (!o) setReturnTarget(null); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
              <ArrowUUpLeft size={20} weight="bold" className="text-[#32D74B]" /> RETURN KEY — <span className="font-mono-data text-[#FACC15]">{returnTarget?.key_value}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Returned By (Name) <span className="text-[#FF3B30]">*</span></Label>
              <Input value={returnForm.returned_by_name} onChange={(e) => setReturnForm({ ...returnForm, returned_by_name: e.target.value })}
                placeholder="Full name"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="km-returned-by-name-input" />
            </div>
            <PhotoCapture label="Returned By (Photo) *" value={returnForm.returned_by_photo}
              onChange={(v) => setReturnForm({ ...returnForm, returned_by_photo: v })} testId="km-returned-by-photo" />
            <p className="text-[10px] text-[#737373]">Date &amp; time will be auto-stamped when you save.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnTarget(null)}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] rounded-sm" data-testid="km-return-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitReturn} disabled={returning}
              className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black rounded-sm font-semibold" data-testid="km-return-save-btn">
              {returning ? "Saving…" : "Mark Returned"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View photo dialog */}
      <Dialog open={!!viewPhoto} onOpenChange={(o) => { if (!o) setViewPhoto(null); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-sm text-white uppercase tracking-wider">{viewPhoto?.label}</DialogTitle>
          </DialogHeader>
          {viewPhoto && (
            <img src={fullUrl(viewPhoto.url)} alt={viewPhoto.label} className="w-full h-auto rounded-sm" />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog (superadmin) */}
      <Dialog open={!!editRec} onOpenChange={(o) => { if (!o) setEditRec(null); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Pencil size={18} className="text-[#007AFF]" /> EDIT KEY RECORD
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!isSuper && (
              <p className="text-[10px] text-[#FACC15] bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-sm px-2 py-1.5" data-testid="km-edit-user-note">
                You can only edit "Taken By" and "Returned By" names. Contact a superadmin to change other fields.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Key Value{!isSuper && " (read-only)"}</Label>
                <Input value={editForm.key_value || ""} onChange={(e) => setEditForm({ ...editForm, key_value: e.target.value })}
                  disabled={!isSuper} readOnly={!isSuper}
                  className={`rounded-sm font-mono-data ${!isSuper ? "bg-[#050505] border-[#1A1A1A] text-[#737373] cursor-not-allowed" : "bg-[#0A0A0A] border-[#262626] text-white"}`}
                  data-testid="km-edit-key-value" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Status{!isSuper && " (read-only)"}</Label>
                <select value={editForm.status || "taken"} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  disabled={!isSuper}
                  className={`border rounded-sm px-3 py-2 text-xs h-9 w-full ${!isSuper ? "bg-[#050505] border-[#1A1A1A] text-[#737373] cursor-not-allowed" : "bg-[#0A0A0A] border-[#262626] text-white"}`}
                  data-testid="km-edit-status">
                  <option value="taken">taken</option>
                  <option value="returned">returned</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Taken By</Label>
                <Input value={editForm.taken_by_name || ""} onChange={(e) => setEditForm({ ...editForm, taken_by_name: e.target.value })}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="km-edit-taken-by" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Taken At{!isSuper && " (read-only)"}</Label>
                <Input type="datetime-local" value={toDatetimeLocal(editForm.taken_at)} onChange={(e) => setEditForm({ ...editForm, taken_at: e.target.value })}
                  disabled={!isSuper} readOnly={!isSuper}
                  className={`rounded-sm font-mono-data ${!isSuper ? "bg-[#050505] border-[#1A1A1A] text-[#737373] cursor-not-allowed" : "bg-[#0A0A0A] border-[#262626] text-white"}`}
                  data-testid="km-edit-taken-at" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Returned By</Label>
                <Input value={editForm.returned_by_name || ""} onChange={(e) => setEditForm({ ...editForm, returned_by_name: e.target.value })}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="km-edit-returned-by" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Returned At{!isSuper && " (read-only)"}</Label>
                <Input type="datetime-local" value={toDatetimeLocal(editForm.returned_at)} onChange={(e) => setEditForm({ ...editForm, returned_at: e.target.value })}
                  disabled={!isSuper} readOnly={!isSuper}
                  className={`rounded-sm font-mono-data ${!isSuper ? "bg-[#050505] border-[#1A1A1A] text-[#737373] cursor-not-allowed" : "bg-[#0A0A0A] border-[#262626] text-white"}`}
                  data-testid="km-edit-returned-at" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Remarks <span className="text-[#737373] normal-case tracking-normal">(optional)</span></Label>
              <Textarea value={editForm.remarks || ""} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                rows={3}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs"
                data-testid="km-edit-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRec(null)}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] rounded-sm" data-testid="km-edit-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitEdit} disabled={savingEdit}
              className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white rounded-sm font-semibold" data-testid="km-edit-save-btn">
              {savingEdit ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-sm text-white uppercase tracking-wider flex items-center gap-2">
              <Trash size={16} className="text-[#FF3B30]" /> DELETE RECORD
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#A3A3A3]">Permanently delete the key record for <span className="text-white font-mono-data">{confirmDelete?.key_value}</span>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] rounded-sm" data-testid="km-delete-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitDelete}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm font-semibold" data-testid="km-delete-confirm-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Labels (superadmin) */}
      <Dialog open={qrLabelsOpen} onOpenChange={(o) => { if (!o) setQrLabelsOpen(false); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Printer size={20} weight="bold" className="text-[#007AFF]" /> KEY QR LABELS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#737373]">Add every physical key here to generate a scannable QR label. Print the labels and stick them onto each key so guards can scan them at hand-out.</p>
            <div className="flex items-end gap-2 flex-wrap p-3 bg-[#0A0A0A] border border-[#262626] rounded-sm">
              <div className="space-y-1 flex-1 min-w-[120px]">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Key Value *</Label>
                <Input value={defForm.key_value} onChange={(e) => setDefForm({ ...defForm, key_value: e.target.value })}
                  placeholder="e.g. K-101" className="bg-[#121212] border-[#262626] text-white rounded-sm font-mono-data" data-testid="km-def-value-input" />
              </div>
              <div className="space-y-1 flex-1 min-w-[160px]">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Label (optional)</Label>
                <Input value={defForm.label} onChange={(e) => setDefForm({ ...defForm, label: e.target.value })}
                  placeholder="e.g. Warehouse Main" className="bg-[#121212] border-[#262626] text-white rounded-sm" data-testid="km-def-label-input" />
              </div>
              <Button onClick={addDefinition} disabled={addingDef}
                className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black rounded-sm text-xs font-semibold" data-testid="km-def-add-btn">
                {addingDef ? "Adding…" : "+ Add Key"}
              </Button>
              <Button onClick={() => { setBulkResult(null); setBulkOpen(true); }}
                variant="outline"
                className="border-[#262626] bg-[#121212] text-white hover:bg-[#1A1A1A] rounded-sm text-xs" data-testid="km-def-bulk-open-btn">
                Bulk Add…
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#A3A3A3]">{definitions.length} key{definitions.length === 1 ? "" : "s"} registered</p>
              <Button onClick={printQrLabels} disabled={definitions.length === 0}
                className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white rounded-sm text-xs font-semibold" data-testid="km-print-labels-btn">
                <Printer size={14} className="mr-1" /> Print QR Labels
              </Button>
            </div>
            {definitions.length === 0 ? (
              <p className="text-xs text-[#737373] text-center py-6">No keys registered yet.</p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {definitions.map((d) => (
                  <div key={d._id} className="p-3 bg-[#0A0A0A] border border-[#262626] rounded-sm text-center" data-testid={`km-def-card-${d._id}`}>
                    <div className="bg-white p-2 rounded-sm inline-block">
                      <QRCodeSVG id={`qr-preview-${d._id}`} value={d.key_value} size={100} />
                    </div>
                    <div className="font-mono-data text-white font-semibold text-sm mt-2">{d.key_value}</div>
                    {d.label && <div className="text-xs text-[#A3A3A3]">{d.label}</div>}
                    <Button size="sm" variant="ghost" onClick={() => deleteDefinition(d._id)}
                      className="text-[#FF3B30] hover:text-[#FF3B30] h-7 text-[10px] mt-1" data-testid={`km-def-delete-btn-${d._id}`}>
                      <Trash size={11} className="mr-1" /> Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Add dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkResult(null); } }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Printer size={18} className="text-[#007AFF]" /> BULK ADD KEYS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#737373]">
              Paste one entry per line as <span className="font-mono-data text-white">key_value</span> or <span className="font-mono-data text-white">key_value,label</span>. Duplicates are skipped automatically.
            </p>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Entries</Label>
              <Textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)}
                rows={10}
                placeholder={"K-101, Warehouse Main\nK-102, Kitchen\nK-103\nK-104, Guest Wing"}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data text-xs"
                data-testid="km-bulk-textarea" />
            </div>
            {bulkResult && (
              <div className="p-3 bg-[#0A0A0A] border border-[#262626] rounded-sm space-y-1 text-xs">
                <p className="text-[#32D74B]">Added: <span className="font-semibold text-white">{bulkResult.added_count}</span></p>
                {bulkResult.skipped_count > 0 && (
                  <p className="text-[#FACC15]">Skipped (already exist): <span className="font-semibold text-white">{bulkResult.skipped_count}</span> — <span className="font-mono-data text-[10px]">{bulkResult.skipped.slice(0, 8).join(", ")}{bulkResult.skipped.length > 8 ? "…" : ""}</span></p>
                )}
                {bulkResult.errors?.length > 0 && (
                  <p className="text-[#FF3B30]">Errors: {bulkResult.errors.length}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkOpen(false); setBulkResult(null); }}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] rounded-sm" data-testid="km-bulk-close-btn">
              Close
            </Button>
            <Button onClick={submitBulk} disabled={bulkBusy || !bulkText.trim()}
              className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white rounded-sm font-semibold" data-testid="km-bulk-submit-btn">
              {bulkBusy ? "Adding…" : "Add All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
